import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { palette } from 'theme/index';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

const ImgWrapper = styled.div`
  img {
    width: 100%;
    background: ${props => props.bgColor || 'none'};
    ${props =>
      props.rounded &&
      css`
        border-radius: 5px;
      `}
    ${props =>
      props.circle &&
      css`
        border-radius: 1000em;
      `}
  }
`;

class ResponsiveImg extends React.PureComponent {
  render() {
    const {
      src,
      defaultWidth,
      maxWidth,
      alt,
      square,
      circle,
      disableUpscale,
      disableLazyLoad,
      quality,
      rounded,
      bgColor
    } = this.props;
    const sizes = [
      320,
      400,
      640,
      768,
      800,
      1024,
      1186,
      1280,
      1366,
      1440,
      1600,
      1750,
      1920
    ];

    const encodedURI =
      (src !== decodeURIComponent(src) ? src : encodeURI(src)) || '';
    const isPng = encodedURI.includes('.png');

    const webpSources = sizes.map(
      (size, i) =>
        `${encodedURI}?format=webply&quality=${quality || '90'}&width=${
          size < maxWidth ? size : maxWidth
        }${
          square || circle
            ? `&height=${size < maxWidth ? size : maxWidth}&fit=crop`
            : ''
        }${disableUpscale ? '&disable=upscale' : ''} ${size}w`
    );

    const defaultSources = sizes.map(
      (size, i) =>
        `${encodedURI}?format=${isPng ? 'png' : 'pjpg'}&quality=${quality ||
          '70'}&width=${size < maxWidth ? size : maxWidth}${
          square || circle
            ? `&height=${size < maxWidth ? size : maxWidth}&fit=crop`
            : ''
        }${disableUpscale ? '&disable=upscale' : ''} ${size}w`
    );

    return (
      <ImgWrapper
        className={this.props.className}
        rounded={rounded}
        circle={circle}
        bgColor={bgColor}
      >
        {src && (
          <PrerenderSafeLazyLoad offset={200} disable={disableLazyLoad} once>
            <picture>
              <source srcSet={webpSources} type="image/webp" />
              <source
                srcSet={defaultSources}
                type={`image/${isPng ? 'png' : 'jpeg'}`}
              />
              <img
                src={`${encodedURI}?format=pjpg&quality=${quality ||
                  '70'}&auto=webp&width=${
                  defaultWidth < maxWidth ? defaultWidth : maxWidth
                }${
                  square
                    ? `&width=${
                        defaultWidth < maxWidth ? defaultWidth : maxWidth
                      }&fit=crop`
                    : ''
                }${disableUpscale ? '&disable=upscale' : ''}`}
                alt={alt}
                style={this.props.style}
                itemProp={this.props.itemProp}
              />
            </picture>
          </PrerenderSafeLazyLoad>
        )}
      </ImgWrapper>
    );
  }
}

ResponsiveImg.defaultProps = {
  defaultWidth: 1200,
  maxWidth: 1920
};

ResponsiveImg.propTypes = {
  src: PropTypes.string,
  defaultWidth: PropTypes.number,
  alt: PropTypes.string,
  square: PropTypes.bool
};

export default ResponsiveImg;

const ResponsiveCardImg = styled(ResponsiveImg)`
  height: 0;
  padding-top: ${props => (props.square ? ' 100%' : '56.25%')};
  overflow: hidden;
  position: relative;
  background: rgba(0, 0, 0, 0.05);
  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
  }
`;

const ResponsiveFeatureImg = styled(ResponsiveImg)`
  img {
    border-radius: 5px;
  }
`;

const ResponsiveInlineImg = styled(ResponsiveImg)`
  img {
    width: inherit;
    max-width: 100%;
  }
`;

const ResponsiveBackgroundImg = styled(({ quality, ...other }) => (
  <ResponsiveImg quality={quality || '40'} {...other} />
))`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  overflow: hidden;
  background: ${props =>
    props.backgroundcolor ? props.backgroundcolor : palette.grey[900]};
  bottom: 0;

  img {
    opacity: ${props => props.opacity || '0.25'};
    z-index: 0;
    width: 100%;
    object-fit: cover;
    height: 100%;
  }
`;

export {
  ResponsiveCardImg,
  ResponsiveFeatureImg,
  ResponsiveBackgroundImg,
  ResponsiveInlineImg
};
